import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase, NotificationsResponse } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { RealtimeChannel } from "@supabase/supabase-js";

/**
 * Hook for setting up real-time subscriptions for notifications
 * with automatic reconnection on session restore and channel errors.
 */
export const useNotificationsRealtime = (viewMode: "active" | "archived") => {
  const queryClient = useQueryClient();
  const notificationChannelRef = useRef<RealtimeChannel | null>(null);
  const readStatusChannelRef = useRef<RealtimeChannel | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);

  const invalidateAllNotificationQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
    queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
    queryClient.invalidateQueries({ queryKey: ['notification-read-status'] });
  }, [queryClient]);

  const cleanupChannels = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (notificationChannelRef.current) {
      supabase.removeChannel(notificationChannelRef.current);
      notificationChannelRef.current = null;
    }
    if (readStatusChannelRef.current) {
      supabase.removeChannel(readStatusChannelRef.current);
      readStatusChannelRef.current = null;
    }
  }, []);

  const setupChannels = useCallback(() => {
    // Clean up any existing channels first
    cleanupChannels();
    retryCountRef.current = 0;

    console.log(`[Realtime] Setting up channels for notifications-list-${viewMode}`);

    const notificationChannel = supabase
      .channel(`notifications-list-${viewMode}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications'
        },
        async (payload) => {
          console.log('Real-time notification change detected:', payload);

          if (payload.eventType === 'INSERT') {
            const newNotification = payload.new as NotificationsResponse;

            if (viewMode === 'active' && newNotification && !newNotification.archived && !newNotification.is_deleted) {
              queryClient.invalidateQueries({ queryKey: ['notifications', 'active'] });
              queryClient.invalidateQueries({ queryKey: ['notifications-count', 'active'] });
              queryClient.invalidateQueries({ queryKey: ['notifications-count', 'unread'] });

              toast.info(`Nieuwe melding: ${newNotification.type}`, {
                description: newNotification.message.substring(0, 50) + (newNotification.message.length > 50 ? '...' : '')
              });
            }
          } else if (payload.eventType === 'UPDATE') {
            const updatedNotification = payload.new as NotificationsResponse;
            const oldNotification = payload.old as NotificationsResponse;

            if (updatedNotification && updatedNotification.is_deleted && !oldNotification.is_deleted) {
              console.log('Notification soft-deleted:', updatedNotification.id);
              queryClient.invalidateQueries({ queryKey: ['notifications'] });
              queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
              return;
            }

            if (updatedNotification) {
              if (updatedNotification.archived && viewMode === 'active') {
                queryClient.invalidateQueries({ queryKey: ['notifications', 'active'] });
                queryClient.invalidateQueries({ queryKey: ['notifications-count', 'active'] });
                queryClient.invalidateQueries({ queryKey: ['notifications-count', 'archived'] });
              } else if (!updatedNotification.archived && viewMode === 'archived') {
                queryClient.invalidateQueries({ queryKey: ['notifications', 'archived'] });
                queryClient.invalidateQueries({ queryKey: ['notifications-count', 'active'] });
                queryClient.invalidateQueries({ queryKey: ['notifications-count', 'archived'] });
              } else {
                queryClient.setQueryData(
                  ['notifications', viewMode],
                  (old: any[] | undefined) => {
                    if (!old) return old;
                    return old.map(notif =>
                      notif.id === updatedNotification.id
                        ? { ...notif, ...updatedNotification }
                        : notif
                    );
                  }
                );
              }

              queryClient.invalidateQueries({
                queryKey: ['notifications-count'],
                refetchType: 'none'
              });

              // Send Telegram via edge function for actie_decoprof status change
              if (updatedNotification.status === 'actie_decoprof' &&
                  oldNotification && oldNotification.status !== 'actie_decoprof') {
                console.log('Status changed to actie_decoprof, triggering Telegram via edge function');
                try {
                  const { data, error } = await supabase.functions.invoke('send-decoprof-telegram', {
                    body: { notification_id: updatedNotification.id, old_status: oldNotification.status }
                  });

                  console.log("Decoprof Telegram result:", data);

                  if (error) {
                    toast.error("De status is gewijzigd, maar het Telegram bericht kon niet worden verzonden.");
                  }
                } catch (telegramError) {
                  console.error("Error sending Decoprof Telegram:", telegramError);
                  toast.error("De status is gewijzigd, maar het Telegram bericht kon niet worden verzonden.");
                }
              }
            }
          } else if (payload.eventType === 'DELETE') {
            queryClient.invalidateQueries({ queryKey: ['notifications', viewMode] });
            queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
          }
        }
      )
      .subscribe((status) => {
        console.log(`[Realtime] Notification channel status: ${status}`);
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          handleReconnect();
        }
        if (status === 'SUBSCRIBED') {
          retryCountRef.current = 0;
        }
      });

    notificationChannelRef.current = notificationChannel;

    const readStatusChannel = supabase
      .channel(`read-status-list-${viewMode}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notification_read_status'
        },
        (payload) => {
          console.log('Real-time read status change detected:', payload);
          queryClient.invalidateQueries({ queryKey: ['notification-read-status'] });
          queryClient.invalidateQueries({ queryKey: ['notifications-count', 'unread'] });
        }
      )
      .subscribe((status) => {
        console.log(`[Realtime] Read status channel status: ${status}`);
      });

    readStatusChannelRef.current = readStatusChannel;
  }, [viewMode, queryClient, cleanupChannels]);

  const handleReconnect = useCallback(() => {
    const delay = Math.min(1000 * Math.pow(2, retryCountRef.current), 30000);
    retryCountRef.current++;
    console.log(`[Realtime] Reconnecting in ${delay}ms (attempt ${retryCountRef.current})`);

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    reconnectTimeoutRef.current = setTimeout(() => {
      setupChannels();
      invalidateAllNotificationQueries();
    }, delay);
  }, [setupChannels, invalidateAllNotificationQueries]);

  useEffect(() => {
    setupChannels();

    // Listen for session-restored event to rebuild channels
    const handleSessionRestored = () => {
      console.log('[Realtime] Session restored, rebuilding channels and refetching data');
      setupChannels();
      invalidateAllNotificationQueries();
    };

    window.addEventListener('session-restored', handleSessionRestored);

    return () => {
      console.log('[Realtime] Cleaning up real-time subscriptions');
      window.removeEventListener('session-restored', handleSessionRestored);
      cleanupChannels();
    };
  }, [setupChannels, invalidateAllNotificationQueries, cleanupChannels]);
};
